import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
  TextField,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import AttachMoney from "@mui/icons-material/AttachMoney";
import Edit from "@mui/icons-material/Edit";
import ToggleOn from "@mui/icons-material/ToggleOn";
import ToggleOff from "@mui/icons-material/ToggleOff";
import {
  useGetPlanPricing,
  useUpdatePlanPricing,
} from "../../hooks/api/usePlan";
import type { PlanPricingItem } from "../../Graphql/types/plan";
import { ROUTE_MEMBERSHIP } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.875rem" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

const PlanPricingDetailView: React.FC = () => {
  const { planId, planPricingId } = useParams<{
    planId: string;
    planPricingId: string;
  }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [item, setItem] = useState<PlanPricingItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [pendingActive, setPendingActive] = useState<boolean | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editCurrency, setEditCurrency] = useState("");

  const getPlanPricing = useGetPlanPricing();
  const updatePlanPricing = useUpdatePlanPricing();

  useEffect(() => {
    if (!planId || !planPricingId) return;
    const pId = decodeURIComponent(planId);
    const ppId = decodeURIComponent(planPricingId);
    getPlanPricing
      .mutateAsync({ planId: pId })
      .then((res) => {
        const list = res.getPlanPricing?.planPricingList ?? [];
        const found = list.find((p) => p.planPricingId === ppId) ?? null;
        setItem(found);
        if (found) {
          setEditAmount(String(found.priceAmount));
          setEditCurrency(found.priceCurrency ?? "RWF");
        }
      })
      .catch(() => {});
  }, [planId, planPricingId]);

  const openEdit = () => {
    if (item) {
      setEditAmount(String(item.priceAmount));
      setEditCurrency(item.priceCurrency ?? "RWF");
      setEditOpen(true);
    }
  };

  const handleSaveEdit = () => {
    if (!planId || !planPricingId || !item) return;
    const amount = parseFloat(editAmount);
    if (Number.isNaN(amount)) return;
    const pId = decodeURIComponent(planId);
    const ppId = decodeURIComponent(planPricingId);
    updatePlanPricing
      .mutateAsync({
        planId: pId,
        planPricingId: ppId,
        input: {
          priceAmount: amount,
          priceCurrency: editCurrency || undefined,
        },
      })
      .then((res) => {
        const updated = res.updatePlanPricing?.planPricing;
        if (updated) setItem(updated);
        setEditOpen(false);
      })
      .catch(() => {});
  };

  const openConfirmStatus = () => {
    if (!item) return;
    setPendingActive(!item.isActive);
    setConfirmStatusOpen(true);
  };

  const pendingStatus = pendingActive;

  const handleConfirmStatus = () => {
    if (!planId || !planPricingId || pendingStatus == null) return;
    const pId = decodeURIComponent(planId);
    const ppId = decodeURIComponent(planPricingId);
    updatePlanPricing
      .mutateAsync({
        planId: pId,
        planPricingId: ppId,
        input: { isActive: pendingStatus },
      })
      .then((res) => {
        const updated = res.updatePlanPricing?.planPricing;
        if (updated) setItem(updated);
        setConfirmStatusOpen(false);
        setPendingActive(null);
      })
      .catch(() => {});
  };

  const handleCloseConfirmStatus = () => {
    setConfirmStatusOpen(false);
    setPendingActive(null);
  };

  if (!planId || !planPricingId) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">
          Missing plan or plan pricing ID.
        </Typography>
      </Box>
    );
  }

  if (!getPlanPricing.isPending && !item) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Plan pricing not found.</Typography>
        <Button
          startIcon={<ArrowBack />}
          sx={{ mt: 1 }}
          onClick={() => navigate(ROUTE_MEMBERSHIP)}
        >
          Back to membership
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <IconButton
        onClick={() => navigate(ROUTE_MEMBERSHIP)}
        sx={{ mb: 2, color: "text.secondary" }}
        aria-label="Back to membership"
      >
        <ArrowBack />
      </IconButton>

      {item && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 100%)`,
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                minWidth={0}
                flex={1}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AttachMoney sx={{ fontSize: 32 }} />
                </Box>
                <Box minWidth={0}>
                  <Typography
                    variant="h5"
                    sx={{ ...responsiveHeading, mb: 0.5 }}
                  >
                    {item.planName} · {item.billingCycleCode}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {item.priceAmount} {item.priceCurrency}
                  </Typography>
                  <Chip
                    label={item.isActive ? "Active" : "Inactive"}
                    size="small"
                    color={item.isActive ? "success" : "default"}
                    sx={{ mt: 1, fontWeight: 600, textTransform: "uppercase" }}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                gap={1.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  color={item.isActive ? "warning" : "primary"}
                  startIcon={item.isActive ? <ToggleOff /> : <ToggleOn />}
                  onClick={openConfirmStatus}
                  disabled={updatePlanPricing.isPending}
                >
                  {item.isActive ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<Edit />}
                  onClick={openEdit}
                >
                  Edit
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Plan pricing details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={3}
            >
              <DetailItem
                label="Plan pricing ID"
                value={item.planPricingId}
                mono
              />
              <DetailItem label="Plan" value={item.planName ?? ""} />
              <DetailItem label="Plan ID" value={item.planId} mono />
              <DetailItem
                label="Billing cycle code"
                value={item.billingCycleCode}
                mono
              />
              <DetailItem
                label="Billing cycle ID"
                value={item.billingCycleId}
                mono
              />
              <DetailItem
                label="Price"
                value={`${item.priceAmount} ${item.priceCurrency}`}
              />
              <DetailItem label="Created" value={formatDate(item.createdAt)} />
              <DetailItem label="Updated" value={formatDate(item.updatedAt)} />
            </Box>
          </Paper>
        </>
      )}

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit plan pricing</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Currency"
              value={editCurrency}
              onChange={(e) => setEditCurrency(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updatePlanPricing.isPending}
          >
            {updatePlanPricing.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmStatusOpen}
        onClose={handleCloseConfirmStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {pendingStatus
            ? "Activate plan pricing?"
            : "Deactivate plan pricing?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {item &&
              (pendingStatus
                ? `Activate this plan pricing (${item.planName} · ${item.billingCycleCode})?`
                : `Deactivate this plan pricing (${item.planName} · ${item.billingCycleCode})?`)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirmStatus} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingStatus ? "primary" : "warning"}
            onClick={handleConfirmStatus}
            disabled={updatePlanPricing.isPending}
          >
            {updatePlanPricing.isPending
              ? "Updating…"
              : pendingStatus
                ? "Activate"
                : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanPricingDetailView;
